/**
 * React's view of the runtime.
 *
 * Every hook here resolves to a `useStore` over an `ExternalStore`, which is the
 * only way a component is allowed to see socket data (plan §2.2). A hook that
 * returned a value derived from a frame would have to run on every frame, and at
 * the 131 f/s peak that is the idiomatic React path this whole design exists to
 * avoid.
 *
 * **A store may not exist yet**, because `StoreSet` is created from `/config`
 * and channel stores are created on first sight of their channel. Hooks cannot
 * be conditional, so an absent store resolves to one shared empty store rather
 * than to a branch: `useStore(store ?? EMPTY)`. Subscribing to it is free — it
 * never notifies.
 */

import { createContext, useContext } from 'react'
import { useStore, useStoreVersion } from './use-store'
import { ExternalStore } from '@/stores/store'
import type { BookState } from '@/stores/book'
import type { PendingOrder } from '@/stores/pending-orders'
import type { Runtime, RuntimeState } from './runtime'

/** Subscribed to whenever the real store does not exist yet. Never notifies. */
const EMPTY = new ExternalStore<never[]>([])

export const RuntimeContext = createContext<Runtime | null>(null)

export function useRuntime(): Runtime {
  const runtime = useContext(RuntimeContext)
  if (!runtime) throw new Error('useRuntime outside <RuntimeProvider>')
  return runtime
}

export function useRuntimeState(): RuntimeState {
  return useStore(useRuntime().state)
}

/**
 * The channel stores, **and a re-render when they come into existence.**
 *
 * `StoreSet` is built from `/config`, so `runtime.stores` is `null` for the
 * first few hundred milliseconds of every tab. Every hook below resolves
 * `store ?? EMPTY` for that window — and `EMPTY` never notifies, which is fine
 * only for a component that re-renders for some *other* reason before the real
 * store arrives.
 *
 * **A component that subscribes a channel store and renders nothing else has no
 * such reason**, so it subscribes `EMPTY` on its first render and stays there
 * forever: no notification, no re-render, no re-subscribe. The header's
 * notification bell is exactly that component, and it never lit — the frame
 * arrived, the store applied it, and nothing on screen was listening (§F28m).
 * The panels on the market page got away with it because the runtime state
 * store re-renders them on `phase`, which happens to re-run their hooks.
 *
 * `Runtime.storesReady` fixes it: a store that notifies exactly once, at the
 * instant `StoreSet` is created, and never again.
 */
function useStores(): Runtime['stores'] {
  const runtime = useRuntime()
  // One re-render, once, when the stores are created. Deliberately not
  // `runtime.state`: that changes on every roll, every connection status and
  // every clock resync, and subscribing every channel hook to it would
  // re-render every panel on all three — the coupling invariant 2 exists to
  // prevent.
  useStore(runtime.storesReady)
  return runtime.stores
}

// ---------------------------------------------------------------------------
// Channel stores
// ---------------------------------------------------------------------------

function useSingleton<T>(store: { store: ExternalStore<unknown> } | undefined): T | null {
  const state = useStore((store?.store ?? EMPTY) as ExternalStore<unknown>)
  return store ? (state as T | null) : null
}

/** `market.{id}` — the state machine's source of truth. */
export function useMarket<T = Record<string, unknown>>(marketId: string | null): T | null {
  const stores = useStores()
  const store = marketId ? stores?.marketFor(marketId) : undefined
  return useSingleton<T>(store)
}

/** `index.{symbol}` — per symbol, and it survives the roll untouched. */
export function useIndex<T = Record<string, unknown>>(symbol: string | null): T | null {
  const stores = useStores()
  const store = symbol ? stores?.indexFor(symbol) : undefined
  return useSingleton<T>(store)
}

/** `ticker.{id}` — a complete replacement every frame, never a delta. */
export function useTicker<T = Record<string, unknown>>(marketId: string | null): T | null {
  const stores = useStores()
  const store = marketId ? stores?.tickerFor(marketId) : undefined
  return useSingleton<T>(store)
}

/** `series.{id}` — the rail. */
export function useSeries<T = Record<string, unknown>>(seriesId: string | null): T | null {
  const stores = useStores()
  const store = seriesId ? stores?.seriesFor(seriesId) : undefined
  return useSingleton<T>(store)
}

/** `trades.{id}` — newest first, bounded. */
export function useTape<T = Record<string, unknown>>(marketId: string | null): readonly T[] {
  const stores = useStores()
  const log = marketId ? stores?.tradesFor(marketId) : undefined
  useStoreVersion((log?.store ?? EMPTY) as ExternalStore<unknown>)
  return (log?.entries ?? []) as readonly T[]
}

/**
 * `chat.{asset}` — the troll box (§F137), newest first, bounded.
 *
 * Returns the version beside the rows for `useSettlements`'s reason: the rows
 * are mutated in place, and the panel keeps the room scrolled to its newest
 * message with an effect that must run on every arrival.
 */
export function useChat<T = Record<string, unknown>>(asset: string | null): {
  rows: readonly T[]
  version: number
} {
  const stores = useStores()
  const log = asset ? stores?.chatFor(asset) : undefined
  const version = useStoreVersion((log?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (log?.entries ?? []) as readonly T[], version }
}

/** `candles.{id}.{interval}` — upsert by `open_time_ms`. */
export function useCandles<T = Record<string, unknown>>(
  marketId: string | null,
  interval = '1s',
): { rows: readonly T[]; version: number } {
  const stores = useStores()
  const store = marketId ? stores?.candlesFor(`candles.${marketId}.${interval}`) : undefined
  const version = useStoreVersion((store?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (store?.list() ?? []) as readonly T[], version }
}

/**
 * `book.{id}`.
 *
 * Returns the **live** state object and the version separately, because the
 * ladder is not reconciled (plan §2.3): the renderer mutates fixed rows against
 * this object rather than mapping it to elements, and the version is only there
 * to say "look again".
 */
export function useBook(marketId: string | null): { book: BookState | null; version: number } {
  const stores = useStores()
  const store = marketId ? stores?.bookFor(marketId) : undefined
  const version = useStoreVersion((store?.store ?? EMPTY) as ExternalStore<unknown>)
  return { book: store?.state ?? null, version }
}

// ---------------------------------------------------------------------------
// Private channels (Phase 6)
//
// Subscribed by the panel that renders them, never by the screen. `useStore` is
// `useSyncExternalStore`, so `useOrders()` in `<TradeRoute>` would re-render the
// chart, the ladder and the countdown on every order update; in the table that
// renders them it re-renders a table. That is invariant 2, and `panels.tsx` is
// the shape it takes.
// ---------------------------------------------------------------------------

/** `orders` — keyed by `order_id`, apply-if-newer on `version`. */
export function useOrders<T = Record<string, unknown>>(): { rows: readonly T[]; version: number } {
  const stores = useStores()
  const store = stores?.orders
  const version = useStoreVersion((store?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (store?.list() ?? []) as readonly T[], version }
}

/** The optimistic rows, keyed by `client_order_id` (`stores/pending-orders.ts`). */
export function usePendingOrders(): { rows: readonly PendingOrder[]; version: number } {
  const stores = useStores()
  const store = stores?.pendingOrders
  const version = useStoreVersion((store?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (store?.list() ?? []) as readonly PendingOrder[], version }
}

/** `positions` — keyed by `market_id`; one object carries both outcomes. */
export function usePositions<T = Record<string, unknown>>(): { rows: readonly T[]; version: number } {
  const stores = useStores()
  const store = stores?.positions
  const version = useStoreVersion((store?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (store?.list() ?? []) as readonly T[], version }
}

/**
 * `fills` — a bounded log, newest first, whose rows **can be revised in place**.
 *
 * **It returns the version, and it did not before §F31.** A fill used to be
 * immutable, so a consumer that only rendered the rows was safe on the array's
 * stable identity alone. It is not immutable any more: under D-B1 the same
 * `fill_id` arrives again at a higher `version` with `settlement_tx_hash`
 * populated, and `LogStore.append` replaces that entry **in place** — the array
 * identity does not change and neither does its length. A `useMemo` or
 * `useEffect` over the rows alone would therefore never see the confirmation,
 * which is invariant 17's failure mode with a new cause (§F25k).
 *
 * Rendering the rows was always fine and still is. Deriving anything from them
 * needs the version in the dependency list.
 */
export function useFills<T = Record<string, unknown>>(): {
  rows: readonly T[]
  version: number
} {
  const stores = useStores()
  const log = stores?.fills
  const version = useStoreVersion((log?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (log?.entries ?? []) as readonly T[], version }
}

/**
 * `settlements` — an append-only log, newest first, deduped `immutable` on
 * `settlement_id`.
 *
 * Subscribed since Phase 6 and rendered since Phase 7. Two consumers:
 * `<SettlementReceiptStack>`, which shows the ones that just arrived, and the
 * Settled tab, which is the durable answer to "did I win?" — because a
 * dismissed toast must not be the only place a user could learn it (§F24f).
 *
 * **It returns the version, and `useTape` does not.** `LogStore.append` does
 * `entries.unshift(...)` — it mutates the array in place, so its identity never
 * changes and a `useEffect` keyed on the rows alone runs exactly once, on mount,
 * when the list is empty. `useTape` gets away with it because the public tape is
 * append-only and its consumer only *renders* the rows, and a render is already
 * driven by `useStoreVersion`. This one has an effect behind it: a settlement
 * arriving is what raises the receipt's toast, and that toast never fired until
 * the version was in the dependency list (register §F25k). `useFills` joined
 * this hook in §F31, when a fill stopped being immutable.
 */
export function useSettlements<T = Record<string, unknown>>(): {
  rows: readonly T[]
  version: number
} {
  const stores = useStores()
  const log = stores?.settlements
  const version = useStoreVersion((log?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (log?.entries ?? []) as readonly T[], version }
}

/** `balance` — always a complete five-component object, never a field delta. */
export function useBalance<T = Record<string, unknown>>(): T | null {
  return useSingleton<T>(useStores()?.balance)
}

/**
 * `notifications` — an append-only log, newest first, and the tray's source.
 *
 * **Returns the version, for the same reason `useSettlements` does.**
 * `LogStore.append` mutates its array in place, so a `useMemo` or `useEffect`
 * keyed on the rows alone runs once, on mount, with an empty list (invariant 17,
 * §F25k). The unread badge is a derived count and it is exactly the shape that
 * freezes.
 */
export function useNotifications<T = Record<string, unknown>>(): {
  rows: readonly T[]
  version: number
} {
  const stores = useStores()
  const log = stores?.notifications
  const version = useStoreVersion((log?.store ?? EMPTY) as ExternalStore<unknown>)
  return { rows: (log?.entries ?? []) as readonly T[], version }
}

/** `heartbeat` — the liveness surface behind the `STALE` pill. */
export function useHeartbeat<T = Record<string, unknown>>(): T | null {
  return useSingleton<T>(useStores()?.heartbeat)
}
